import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import confetti from 'canvas-confetti'
import { useAuth } from '@/hooks/useAuth'
import { sendPhoneCodeThunk, verifyPhoneCodeThunk, updateUser } from '@/redux/userSlice'
import type { AppDispatch } from '@/store'
import { ProfilePhotoUpload } from '@adthlete/shared-frontend'
import PreferenceMultiSelect from '@/components/PreferenceMultiSelect'
import SportsMultiSelect from '@/components/SportsMultiSelect'
import { searchSchools } from '@/lib/api'
import athleteApi from '@/lib/api'
import { Button } from '@/components/ui/button'
import { INPUT_CLASSES } from '@/lib/theme'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/sonner'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { ChevronsUpDown, Check, CheckCircle2, Mail } from 'lucide-react'
import { interestSuggestions, contentStyleSuggestions } from '@/config'

const TOTAL_STEPS = 5

// ── Progress indicator ────────────────────────────────────────────────────────

const StepIndicator = ({ current, total }: { current: number; total: number }) => (
  <div className="flex items-center justify-center mb-8">
    {Array.from({ length: total }).map((_, i) => (
      <React.Fragment key={i}>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
            i < current
              ? 'bg-[#C0FD72] text-black'
              : i === current
                ? 'bg-[#C0FD72]/20 border-2 border-[#C0FD72] text-[#C0FD72]'
                : 'bg-white/10 border border-white/20 text-gray-500'
          }`}
        >
          {i < current ? <Check className="h-4 w-4" /> : i + 1}
        </div>
        {i < total - 1 && (
          <div
            className={`h-0.5 w-8 mx-1 transition-all duration-300 ${i < current ? 'bg-[#C0FD72]' : 'bg-white/10'}`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
)

// ── Main component ────────────────────────────────────────────────────────────

const WelcomeWalkthrough: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, updateProfile, updateSchool, updateSports, updatePreferences, updateAvatar } =
    useAuth()
  const dispatch = useDispatch<AppDispatch>()

  const [currentStep, setCurrentStep] = useState(0)
  const [isSaving, setIsSaving] = useState(false)

  // ── Step 1 state ─────────────────────────────────────────────────────────
  const [name, setName] = useState('')
  const [selectedSportIds, setSelectedSportIds] = useState<number[]>([])
  const [selectedSchool, setSelectedSchool] = useState<{ id: number; name: string } | null>(null)
  const [schoolOpen, setSchoolOpen] = useState(false)
  const [schoolSearch, setSchoolSearch] = useState('')
  const [schoolResults, setSchoolResults] = useState<{ id: number; name: string }[]>([])
  const schoolSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ── Step 2 state ─────────────────────────────────────────────────────────
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>(undefined)

  // ── Step 3 state ─────────────────────────────────────────────────────────
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [phoneLoading, setPhoneLoading] = useState(false)

  // ── Step 4 state ─────────────────────────────────────────────────────────
  const [managerEmail, setManagerEmail] = useState('')
  const [managerInviteSent, setManagerInviteSent] = useState(false)
  const [managerInviteLoading, setManagerInviteLoading] = useState(false)

  // ── Step 5 state ─────────────────────────────────────────────────────────
  const [interests, setInterests] = useState<string[]>([])
  const [contentStyles, setContentStyles] = useState<string[]>([])

  // ── Seed from Redux athlete data ─────────────────────────────────────────
  useEffect(() => {
    if (!athlete) return
    setName(athlete.name || '')
    setProfilePhoto(athlete.avatar || undefined)
    setPhone(athlete.phone || '')
    setPhoneVerified(athlete.phone_verified || false)

    if (athlete.school && athlete.school_id) {
      setSelectedSchool({ id: athlete.school_id, name: athlete.school.name })
    }

    if (athlete.sports && Array.isArray(athlete.sports) && athlete.sports.length > 0) {
      const first = athlete.sports[0]
      if (first !== null && typeof first === 'object' && 'id' in (first as object)) {
        setSelectedSportIds((athlete.sports as any[]).map((s) => s.id))
      } else {
        setSelectedSportIds(athlete.sports as number[])
      }
    }

    if (athlete.preferences) {
      setInterests(athlete.preferences.interests || [])
      setContentStyles(athlete.preferences.contentStyles || [])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athlete?.id])

  // ── Fire confetti on completion ──────────────────────────────────────────
  useEffect(() => {
    if (currentStep === TOTAL_STEPS) {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 } })
      setTimeout(
        () => confetti({ particleCount: 60, angle: 60, spread: 55, origin: { x: 0 } }),
        250,
      )
      setTimeout(
        () => confetti({ particleCount: 60, angle: 120, spread: 55, origin: { x: 1 } }),
        400,
      )
    }
  }, [currentStep])

  // ── Navigation helpers ───────────────────────────────────────────────────
  const hasManager = !!(athlete as any)?.proxy_claimed_at
  const advance = () =>
    setCurrentStep((s) => {
      const next = s + 1
      return next === 3 && hasManager ? 4 : next
    })
  const back = () =>
    setCurrentStep((s) => {
      const prev = s - 1
      return prev === 3 && hasManager ? 2 : prev
    })

  // ── School search ────────────────────────────────────────────────────────
  const handleSchoolSearch = (value: string) => {
    setSchoolSearch(value)
    if (schoolSearchTimer.current) clearTimeout(schoolSearchTimer.current)
    if (value.trim().length < 2) {
      setSchoolResults([])
      return
    }
    schoolSearchTimer.current = setTimeout(async () => {
      try {
        const results = await searchSchools(value.trim())
        setSchoolResults(results)
      } catch {
        setSchoolResults([])
      }
    }, 300)
  }

  const handleSchoolSelect = (school: { id: number; name: string }) => {
    setSelectedSchool(school)
    setSchoolOpen(false)
    setSchoolSearch('')
    setSchoolResults([])
  }

  // ── Step 1 save ──────────────────────────────────────────────────────────
  const handleStep1Continue = async () => {
    setIsSaving(true)
    try {
      if (name !== athlete?.name) {
        await updateProfile({ name })
      }
      if (selectedSchool && selectedSchool.id !== athlete?.school_id) {
        await updateSchool(selectedSchool.id)
      }
      if (selectedSportIds.length > 0) {
        await updateSports(selectedSportIds)
      }
      advance()
    } catch {
      toast.error('Error', { description: 'Failed to save. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  // ── Step 2 photo ─────────────────────────────────────────────────────────
  const handlePhotoChange = async (photo: string) => {
    setProfilePhoto(photo)
    try {
      await updateAvatar(photo)
    } catch {
      toast.error('Error', { description: 'Failed to update photo.' })
    }
  }

  const handlePhotoRemove = async () => {
    const defaultAvatar = 'https://i.pravatar.cc/150?img=11'
    setProfilePhoto(defaultAvatar)
    try {
      await updateAvatar(defaultAvatar)
    } catch {
      toast.error('Error', { description: 'Failed to remove photo.' })
    }
  }

  // ── Step 3 phone ─────────────────────────────────────────────────────────
  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Invalid phone number', { description: 'Please enter a valid phone number.' })
      return
    }
    setPhoneLoading(true)
    try {
      await dispatch(sendPhoneCodeThunk(phone)).unwrap()
      setCodeSent(true)
      toast('Code sent', { description: `Verification code sent to ${phone}` })
    } catch (err: any) {
      toast.error('Error', { description: err || 'Failed to send verification code.' })
    } finally {
      setPhoneLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!phoneCode || phoneCode.length < 6) {
      toast.error('Invalid code', { description: 'Please enter a 6-digit code.' })
      return
    }
    setPhoneLoading(true)
    try {
      await dispatch(verifyPhoneCodeThunk({ phone, code: phoneCode })).unwrap()
      setPhoneVerified(true)
      setCodeSent(false)
      setPhoneCode('')
      toast('Phone verified!', { description: 'Your phone number has been verified.' })
    } catch (err: any) {
      toast.error('Verification failed', { description: err || 'Invalid code.' })
    } finally {
      setPhoneLoading(false)
    }
  }

  // ── Step 4 manager ───────────────────────────────────────────────────────
  const handleInviteManager = async () => {
    if (!managerEmail) return
    setManagerInviteLoading(true)
    try {
      const response = await athleteApi.post('/athlete/managers/connect', { email: managerEmail })
      setManagerInviteSent(true)
      toast(response.status === 202 ? 'Invitation sent' : 'Connection request sent', {
        description:
          response.status === 202
            ? "They'll get an email to join Athios and will be connected to you automatically."
            : `Request sent to ${managerEmail}`,
      })
    } catch (err: any) {
      const msg: string = err.response?.data?.error || 'Failed to send invitation'
      toast.error('Error', { description: msg })
    } finally {
      setManagerInviteLoading(false)
    }
  }

  // ── Step 5 finish ────────────────────────────────────────────────────────
  const handleFinish = async () => {
    setIsSaving(true)
    try {
      await Promise.all([
        updatePreferences('interests', interests),
        updatePreferences('contentStyles', contentStyles),
      ])
      advance()
    } catch {
      toast.error('Error', { description: 'Failed to save preferences. Please try again.' })
    } finally {
      setIsSaving(false)
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-8rem)] py-8">
      <div className="w-full max-w-2xl">
        {/* ── Completion screen ─────────────────────────────────────────── */}
        {currentStep === TOTAL_STEPS && (
          <div className="bg-white/10 backdrop-blur-xs border border-white/5 shadow-xl rounded-2xl p-8 text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex justify-center mb-6">
              <CheckCircle2 className="w-20 h-20 text-[#C0FD72]" />
            </div>
            <h1 className="text-4xl font-black text-white tracking-tight mb-3">You're all set!</h1>
            <p className="text-gray-400 text-lg mb-8">
              Your profile is set up and you're ready to start connecting with brands.
            </p>
            <Button
              onClick={async () => {
                try {
                  await athleteApi.post('/athlete/complete-welcome')
                } catch {
                  // Non-fatal — proceed regardless
                }
                dispatch(updateUser({ welcome_completed: true }))
                navigate('/')
              }}
              variant="lime"
              className="w-full h-12 text-base font-semibold"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* ── Walk-through card ──────────────────────────────────────────── */}
        {currentStep < TOTAL_STEPS && (
          <div className="bg-white/10 backdrop-blur-xs border border-white/5 shadow-xl rounded-2xl p-8">
            <StepIndicator current={currentStep} total={TOTAL_STEPS} />

            {/* ── Step 1: The Basics ──────────────────────────────────────── */}
            {currentStep === 0 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">The Basics</h2>
                <p className="text-gray-400 mb-6">Let's start with a few details about you.</p>

                <div className="space-y-5">
                  {/* Display Name */}
                  <div className="space-y-2">
                    <Label className="text-white">Display Name</Label>
                    <Input
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={INPUT_CLASSES.dark}
                    />
                  </div>

                  {/* School */}
                  <div className="space-y-2">
                    <Label className="text-white">School</Label>
                    <Popover open={schoolOpen} onOpenChange={setSchoolOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={schoolOpen}
                          className="w-full h-10 justify-between bg-white/5 text-white border-white/10 hover:bg-white/10 hover:text-white font-normal"
                        >
                          <span className={selectedSchool ? 'text-white' : 'text-gray-400'}>
                            {selectedSchool ? selectedSchool.name : 'Search for your school...'}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-400" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-full p-0 bg-[#2a3a48] border-white/10"
                        style={{ width: 'var(--radix-popover-trigger-width)' }}
                      >
                        <Command className="bg-transparent">
                          <CommandInput
                            placeholder="Search school..."
                            value={schoolSearch}
                            onValueChange={handleSchoolSearch}
                            className="text-white placeholder:text-gray-400 border-b border-white/10"
                          />
                          <CommandList>
                            {schoolSearch.trim().length < 2 ? (
                              <CommandEmpty className="text-gray-400 py-3 text-sm text-center">
                                Type at least 2 characters to search.
                              </CommandEmpty>
                            ) : schoolResults.length === 0 ? (
                              <CommandEmpty className="text-gray-400 py-3 text-sm text-center">
                                No schools found.
                              </CommandEmpty>
                            ) : (
                              <CommandGroup>
                                {schoolResults.map((school) => (
                                  <CommandItem
                                    key={school.id}
                                    value={school.name}
                                    onSelect={() => handleSchoolSelect(school)}
                                    className="text-white hover:bg-white/10 cursor-pointer"
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${selectedSchool?.id === school.id ? 'opacity-100' : 'opacity-0'}`}
                                    />
                                    {school.name}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Sports */}
                  <div className="space-y-2">
                    <SportsMultiSelect
                      label="Sports"
                      description="Select all sports you participate in"
                      selectedSportIds={selectedSportIds}
                      onSportsChange={setSelectedSportIds}
                      placeholder="Add a sport..."
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mt-8">
                  <div />
                  <Button
                    onClick={handleStep1Continue}
                    disabled={isSaving || !name.trim()}
                    variant="lime"
                    className="font-semibold px-8"
                  >
                    {isSaving ? 'Saving...' : 'Continue'}
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step 2: Photo ───────────────────────────────────────────── */}
            {currentStep === 1 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                  Time For Your Close-Up
                </h2>
                <p className="text-gray-400 mb-6">
                  Add a photo so brands know who they're working with.
                </p>

                <ProfilePhotoUpload
                  currentPhoto={profilePhoto}
                  onPhotoChange={handlePhotoChange}
                  onPhotoRemove={handlePhotoRemove}
                  onSuccess={(msg) => toast('Success', { description: msg })}
                  onError={(err) => toast.error('Error', { description: err.message })}
                />

                <div className="flex items-center justify-between mt-8">
                  <Button variant="ghost" onClick={back} className="text-gray-400">
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={advance}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Skip
                    </button>
                    <Button onClick={advance} variant="lime" className="font-semibold px-8">
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 3: Phone ───────────────────────────────────────────── */}
            {currentStep === 2 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                  Verify Your Phone
                </h2>
                <p className="text-gray-400 mb-6">
                  Get notified about new opportunities, collaboration updates, and account
                  activity. We'll never share or sell your number.
                </p>

                {phoneVerified ? (
                  <div className="flex items-center gap-2 p-4 bg-[#C0FD72]/10 border border-[#C0FD72]/30 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-[#C0FD72]" />
                    <span className="text-[#C0FD72] font-medium">Phone number verified</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Phone number"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={`${INPUT_CLASSES.dark} flex-1`}
                        disabled={codeSent || phoneLoading}
                        type="tel"
                        autoComplete="tel"
                      />
                      {!codeSent && (
                        <Button
                          type="button"
                          variant="outline"
                          className="border-[#C0FD72] text-[#C0FD72] hover:bg-[#C0FD72] hover:text-black whitespace-nowrap"
                          onClick={handleSendCode}
                          disabled={phoneLoading || !phone}
                        >
                          {phoneLoading ? 'Sending...' : 'Send Code'}
                        </Button>
                      )}
                    </div>

                    {!codeSent && (
                      <p className="text-xs text-gray-500 leading-relaxed">
                        By adding your phone number and tapping{' '}
                        <span className="text-gray-400">Send Code</span>, you agree to receive a
                        one-time SMS verification code from Athios. Standard messaging rates may
                        apply.
                      </p>
                    )}

                    {codeSent && (
                      <div className="flex flex-col space-y-2 animate-in fade-in slide-in-from-top-2">
                        <Label className="text-white">Verification Code</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Enter 6-digit code"
                            value={phoneCode}
                            onChange={(e) => setPhoneCode(e.target.value)}
                            className={`${INPUT_CLASSES.dark} flex-1`}
                            maxLength={6}
                            autoComplete="one-time-code"
                            inputMode="numeric"
                          />
                          <Button
                            type="button"
                            variant="lime"
                            className="whitespace-nowrap"
                            onClick={handleVerifyCode}
                            disabled={phoneLoading || !phoneCode}
                          >
                            {phoneLoading ? 'Verifying...' : 'Verify'}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            className="text-gray-400 whitespace-nowrap"
                            onClick={() => setCodeSent(false)}
                            disabled={phoneLoading}
                          >
                            Cancel
                          </Button>
                        </div>
                        <p className="text-xs text-gray-500">
                          Check your messages for the 6-digit code. It may take a moment to arrive.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-8">
                  <Button variant="ghost" onClick={back} className="text-gray-400">
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    {!phoneVerified && (
                      <button
                        onClick={advance}
                        className="text-sm text-gray-400 hover:text-white transition-colors"
                      >
                        Skip
                      </button>
                    )}
                    <Button onClick={advance} variant="lime" className="font-semibold px-8">
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 4: Manager ─────────────────────────────────────────── */}
            {currentStep === 3 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                  Have a Manager? Invite Them
                </h2>
                <p className="text-gray-400 mb-6">
                  Connect with your talent manager so they can help manage your brand deals. If
                  they don't have an account yet, we'll email them an invite.
                </p>

                {managerInviteSent ? (
                  <div className="flex items-center gap-3 p-4 bg-[#C0FD72]/10 border border-[#C0FD72]/30 rounded-lg">
                    <Mail className="h-5 w-5 text-[#C0FD72]" />
                    <span className="text-[#C0FD72] font-medium">
                      Invitation sent to {managerEmail}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      placeholder="manager@example.com"
                      value={managerEmail}
                      onChange={(e) => setManagerEmail(e.target.value)}
                      className={`${INPUT_CLASSES.dark} flex-1`}
                    />
                    <Button
                      onClick={handleInviteManager}
                      disabled={managerInviteLoading || !managerEmail}
                      variant="lime"
                      className="whitespace-nowrap font-semibold"
                    >
                      {managerInviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between mt-8">
                  <Button variant="ghost" onClick={back} className="text-gray-400">
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={advance}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Skip
                    </button>
                    <Button onClick={advance} variant="lime" className="font-semibold px-8">
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step 5: Interests ───────────────────────────────────────── */}
            {currentStep === 4 && (
              <div className="animate-in fade-in slide-in-from-right-4 duration-300">
                <h2 className="text-2xl font-black text-white tracking-tight mb-1">
                  Tell Us About Yourself
                </h2>
                <p className="text-gray-400 mb-6">
                  Help brands find you by sharing your interests and content style.
                </p>

                <div className="space-y-6">
                  <PreferenceMultiSelect
                    label="Interests"
                    description="What topics are you passionate about?"
                    selectedItems={interests}
                    onItemsChange={setInterests}
                    placeholder="Add a topic..."
                    suggestions={interestSuggestions}
                    helperText="Type a value and press Enter, or click a suggestion to add."
                    showHelperOnFocusOnly
                    reserveHelperSpace
                  />

                  <PreferenceMultiSelect
                    label="Content Style"
                    description="What types of content do you enjoy creating?"
                    selectedItems={contentStyles}
                    onItemsChange={setContentStyles}
                    placeholder="Add a content style..."
                    suggestions={contentStyleSuggestions}
                    helperText="Type a value and press Enter, or click a suggestion to add."
                    showHelperOnFocusOnly
                    reserveHelperSpace
                  />
                </div>

                <div className="flex items-center justify-between mt-8">
                  <Button variant="ghost" onClick={back} className="text-gray-400">
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={advance}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Skip
                    </button>
                    <Button
                      onClick={handleFinish}
                      disabled={isSaving}
                      variant="lime"
                      className="font-semibold px-8"
                    >
                      {isSaving ? 'Saving...' : 'Finish'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default WelcomeWalkthrough
